import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Check, X, Clock, Sparkles, Heart, Truck, 
  DollarSign, Tag, ToggleLeft, ToggleRight, RefreshCw, Zap, Star,
  Calendar, Moon, Snowflake, Flame, LayoutGrid, Shirt, Car, Home,
  ShoppingCart, Dog, PenTool, Camera, AlertTriangle, RotateCcw, MapPin,
  UtensilsCrossed, Square, ChevronDown, ChevronUp, Package
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { addonsAPI } from '../../services/api';

// Icon mapping
const iconMap = {
  Zap, Star, Clock, Calendar, Moon, Snowflake, Flame, LayoutGrid, Shirt, Car,
  Home, ShoppingCart, Dog, PenTool, Camera, AlertTriangle, RotateCcw, MapPin,
  UtensilsCrossed, Square, Heart, Truck, Sparkles, Plus, Package, Tag, DollarSign
};

const AddOnsTab = () => {
  const [addons, setAddons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    price_type: 'flat',
    category: 'general',
    applies_to: [],
    is_active: true,
    requires_approval: false,
    max_quantity: 1,
    icon: 'Plus'
  });

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [addonsRes, categoriesRes, statsRes] = await Promise.all([
        addonsAPI.getAll(selectedCategory === 'all' ? null : selectedCategory),
        addonsAPI.getCategories(),
        addonsAPI.getStats()
      ]);
      setAddons(addonsRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
      
      // Expand all categories by default
      const expanded = {};
      categoriesRes.data.forEach(c => { expanded[c.category] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error fetching add-ons:', error);
      // If no add-ons exist, offer to seed defaults
      if (error.response?.status === 404 || addons.length === 0) {
        toast.info('No add-ons found. Click "Seed Defaults" to create starter add-ons.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await addonsAPI.seedDefaults();
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        max_quantity: parseInt(formData.max_quantity) || 1
      };

      if (editingAddon) {
        await addonsAPI.update(editingAddon.id, data);
        toast.success('Add-on updated');
      } else {
        await addonsAPI.create(data);
        toast.success('Add-on created');
      }

      setShowCreateModal(false);
      setEditingAddon(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save add-on');
    }
  };

  const handleToggle = async (addon) => {
    try {
      await addonsAPI.toggle(addon.id);
      toast.success(`Add-on ${addon.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const handleDelete = async (addon) => {
    if (!window.confirm(`Delete "${addon.name}"? This cannot be undone.`)) return;
    
    try {
      await addonsAPI.delete(addon.id);
      toast.success('Add-on deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete add-on');
    }
  };

  const openEdit = (addon) => {
    setFormData({
      name: addon.name,
      description: addon.description || '',
      price: addon.price.toString(),
      price_type: addon.price_type,
      category: addon.category,
      applies_to: addon.applies_to || [],
      is_active: addon.is_active,
      requires_approval: addon.requires_approval,
      max_quantity: addon.max_quantity,
      icon: addon.icon || 'Plus'
    });
    setEditingAddon(addon);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      price_type: 'flat',
      category: 'general',
      applies_to: [],
      is_active: true,
      requires_approval: false,
      max_quantity: 1,
      icon: 'Plus'
    });
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      timing: Clock,
      rides: Car,
      laundry: Shirt,
      courier: Truck,
      errands: Package,
      general: Tag
    };
    return icons[category] || Tag;
  };

  const getCategoryColor = (category) => {
    const colors = {
      timing: 'text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30',
      rides: 'text-purple-500 bg-purple-50 border-purple-200',
      laundry: 'text-pink-500 bg-pink-50 border-pink-200',
      courier: 'text-blue-500 bg-blue-50 border-blue-200',
      errands: 'text-amber-500 bg-amber-50 border-amber-200',
      general: 'text-[var(--text-muted)] bg-[var(--background-subtle)] border-[var(--border-primary)]'
    };
    return colors[category] || colors.general;
  };

  const formatPrice = (addon) => {
    const price = `$${addon.price.toFixed(2)}`;
    if (addon.price_type === 'percentage') return `${addon.price}%`;
    if (addon.price_type === 'per_hour') return `${price}/hr`;
    if (addon.price_type === 'per_window') return `${price}/window`;
    if (addon.price_type === 'per_stop') return `${price}/stop`;
    return price;
  };

  // Group addons by category
  const groupedAddons = addons.reduce((acc, addon) => {
    const cat = addon.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(addon);
    return acc;
  }, {});

  const serviceTypes = [
    { value: 'rides', label: 'Rides' },
    { value: 'courier', label: 'Courier' },
    { value: 'laundry', label: 'Laundry' },
    { value: 'errands', label: 'Errands' }
  ];

  const iconOptions = Object.keys(iconMap);

  return (
    <div className="space-y-6" data-testid="addons-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Package className="w-7 h-7 text-[var(--primary)]" />
            Service Add-Ons
          </h2>
          <p className="text-[var(--text-muted)] text-sm">Manage optional extras customers can add to bookings</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          {addons.length === 0 && (
            <Button 
              variant="outline"
              onClick={handleSeedDefaults}
              className="flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Seed Defaults
            </Button>
          )}
          <Button 
            onClick={() => { resetForm(); setEditingAddon(null); setShowCreateModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-addon-btn"
          >
            <Plus className="w-4 h-4" />
            Create Add-On
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{stats.total_addons}</p>
            <p className="text-xs text-[var(--text-muted)]">Total Add-Ons</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.active_addons}</p>
            <p className="text-xs text-[var(--text-muted)]">Active</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{stats.total_usage}</p>
            <p className="text-xs text-[var(--text-muted)]">Times Used</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">${stats.estimated_revenue?.toFixed(0) || 0}</p>
            <p className="text-xs text-[var(--text-muted)]">Est. Revenue</p>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            selectedCategory === 'all' 
              ? 'bg-[var(--primary)] text-white' 
              : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
          }`}
        >
          All Add-Ons
        </button>
        {categories.map(cat => {
          const Icon = getCategoryIcon(cat.category);
          return (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                selectedCategory === cat.category 
                  ? 'bg-[var(--primary)] text-white' 
                  : 'bg-[var(--background-subtle)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {cat.label} ({cat.count})
            </button>
          );
        })}
      </div>

      {/* Add-Ons List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[var(--primary)]" />
          <p className="mt-2 text-[var(--text-muted)]">Loading add-ons...</p>
        </div>
      ) : addons.length === 0 ? (
        <div className="text-center py-12 card">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-[var(--text-secondary)]">No Add-Ons Found</h3>
          <p className="text-[var(--text-muted)] mb-4">
            Create custom add-ons or seed default options to get started.
          </p>
          <Button onClick={handleSeedDefaults} className="btn-primary">
            <Zap className="w-4 h-4 mr-2" />
            Seed Default Add-Ons
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedAddons).map(([category, categoryAddons]) => {
            const Icon = getCategoryIcon(category);
            const colorClass = getCategoryColor(category);
            const catInfo = categories.find(c => c.category === category);
            
            return (
              <div key={category} className="card overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full p-4 flex items-center justify-between border-b ${colorClass}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <span className="font-bold">{catInfo?.label || category}</span>
                    <span className="px-2 py-0.5 bg-[var(--background-paper)]/50 rounded-full text-xs">
                      {categoryAddons.length} add-ons
                    </span>
                  </div>
                  {expandedCategories[category] ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>

                {/* Add-Ons Grid */}
                {expandedCategories[category] && (
                  <div className="p-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryAddons.map(addon => {
                      const AddonIcon = iconMap[addon.icon] || Tag;
                      return (
                        <div 
                          key={addon.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            addon.is_active 
                              ? 'border-[var(--border-primary)] bg-[var(--background-paper)] hover:border-[var(--primary)]/30' 
                              : 'border-gray-100 bg-[var(--background-subtle)] opacity-60'
                          }`}
                          data-testid={`addon-${addon.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClass}`}>
                                <AddonIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-sm">{addon.name}</h4>
                                <p className="text-lg font-bold text-[var(--primary)]">
                                  {formatPrice(addon)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleToggle(addon)}
                              className={`p-1 rounded-full transition-colors ${
                                addon.is_active 
                                  ? 'text-[var(--success)] hover:bg-[var(--success)]/10' 
                                  : 'text-gray-400 hover:bg-[var(--background-subtle)]'
                              }`}
                              title={addon.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {addon.is_active ? (
                                <ToggleRight className="w-6 h-6" />
                              ) : (
                                <ToggleLeft className="w-6 h-6" />
                              )}
                            </button>
                          </div>

                          {addon.description && (
                            <p className="text-xs text-[var(--text-muted)] mb-3 line-clamp-2">
                              {addon.description}
                            </p>
                          )}

                          {/* Tags */}
                          <div className="flex flex-wrap gap-1 mb-3">
                            {addon.applies_to?.length > 0 ? (
                              addon.applies_to.map(st => (
                                <span key={st} className="px-2 py-0.5 bg-[var(--background-subtle)] rounded text-[10px] text-[var(--text-secondary)]">
                                  {st}
                                </span>
                              ))
                            ) : (
                              <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-[10px]">
                                All Services
                              </span>
                            )}
                            {addon.max_quantity > 1 && (
                              <span className="px-2 py-0.5 bg-[#F4C2C2]/10 text-[#F4C2C2] rounded text-[10px]">
                                Max: {addon.max_quantity}
                              </span>
                            )}
                          </div>

                          {/* Usage Stats */}
                          {addon.usage_count > 0 && (
                            <p className="text-[10px] text-[var(--text-muted)] mb-2">
                              Used {addon.usage_count} times
                            </p>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openEdit(addon)}
                              className="flex-1"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDelete(addon)}
                              className="text-[var(--error)] hover:bg-[var(--error)]/10"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)]">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Package className="w-6 h-6 text-[var(--primary)]" />
                {editingAddon ? 'Edit Add-On' : 'Create Add-On'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Same Day Service"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What does this add-on include?"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                  rows={2}
                />
              </div>

              {/* Price & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="0.00"
                      className="w-full p-3 pl-8 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price Type</label>
                  <select
                    value={formData.price_type}
                    onChange={(e) => setFormData({ ...formData, price_type: e.target.value })}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                  >
                    <option value="flat">Flat Fee</option>
                    <option value="percentage">Percentage</option>
                    <option value="per_hour">Per Hour</option>
                    <option value="per_window">Per Window</option>
                    <option value="per_stop">Per Stop</option>
                  </select>
                </div>
              </div>

              {/* Category & Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="timing">Timing & Scheduling</option>
                    <option value="rides">Rides Extras</option>
                    <option value="courier">Courier Extras</option>
                    <option value="laundry">Laundry Extras</option>
                    <option value="errands">Errands Extras</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Icon</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Applies To */}
              <div>
                <label className="block text-sm font-medium mb-1">Applies To</label>
                <p className="text-xs text-[var(--text-muted)] mb-2">Leave empty to apply to all services</p>
                <div className="flex flex-wrap gap-2">
                  {serviceTypes.map(st => (
                    <label key={st.value} className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-[var(--background-subtle)]">
                      <input
                        type="checkbox"
                        checked={formData.applies_to.includes(st.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, applies_to: [...formData.applies_to, st.value] });
                          } else {
                            setFormData({ ...formData, applies_to: formData.applies_to.filter(v => v !== st.value) });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{st.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Max Quantity */}
              <div>
                <label className="block text-sm font-medium mb-1">Max Quantity</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_quantity}
                  onChange={(e) => setFormData({ ...formData, max_quantity: parseInt(e.target.value) || 1 })}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">How many times can this be added to a booking?</p>
              </div>

              {/* Options */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData({ ...formData, requires_approval: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Requires Approval</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => { setShowCreateModal(false); setEditingAddon(null); resetForm(); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  {editingAddon ? 'Update Add-On' : 'Create Add-On'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnsTab;
